import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import PricingTableFieldCell from './PricingTableFieldCell';

const TableRow = styled.tr`
  border-bottom: 1px solid ${(props) => props.theme?.borderColor || '#e2e8f0'};
  transition: background-color 0.2s;
  background-color: ${(props) => (props.isHighlighted ? props.theme?.secondaryColor || '#f7fafc' : 'transparent')};
  position: relative;
`;

const TableCell = styled.td`
  padding: 0;
  margin: 0;
  position: relative;
  text-align: right;
  height: 100%;
  background-color: transparent;
  width: 15px;
`;

const ControlSelect = styled.select`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${(props) => props.theme?.fontColorSecondary || '#718096'};
  font-size: 12px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 20px;
  
  &:hover {
    color: ${(props) => props.theme?.primaryColor || '#4299e1'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.5);
  }

  option {
    background: white;
    color: ${(props) => props.theme?.fontColorPrimary || '#2d3748'};
  }
`;

const PricingTableFieldRow = ({
  rowIndex,
  columns,
  onCellChange,
  onDeleteRow,
  onInsertRowBelow,
  mode,
  formMode,
  isEditable = true,
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  backgroundColor,
  borderColor,
  borderWidth,
  borderRadius,
  padding,
  theme
}) => {
  const visibleColumns = columns.filter((column) => !column.hidden);

  // Show controls whenever the field is editable, regardless of mode
  // This allows row operations in any mode as long as the field isn't read-only or disabled
  const showControls = isEditable;

  const handleControlChange = (e) => {
    const action = e.target.value;
    if (action === 'insert') {
      onInsertRowBelow(rowIndex);
    } else if (action === 'delete') {
      onDeleteRow(rowIndex);
    }
    // Reset select to default option
    e.target.value = '';
  };

  return (
    <TableRow
      theme={theme}
    >
      {visibleColumns.map((column, visibleColumnIndex) => {
        // Find the original column index for proper data handling
        const originalColumnIndex = columns.findIndex((col) => col._id === column._id);
        const cell = column.cells[rowIndex] || {};
        const isFirstColumn = visibleColumnIndex === 0;

        return (
          <PricingTableFieldCell
            key={`${rowIndex}-${column._id}`}
            cell={cell}
            column={column}
            columnIndex={originalColumnIndex}
            rowIndex={rowIndex}
            isFirstColumn={isFirstColumn}
            onCellChange={onCellChange}
            allColumns={columns}
            mode={mode}
            formMode={formMode}
            isEditable={isEditable}
            fontSize={fontSize}
            fontColor={fontColor}
            fontStyle={fontStyle}
            fontWeight={fontWeight}
            textAlign={textAlign}
            textTransform={textTransform}
            textDecoration={textDecoration}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            borderWidth={borderWidth}
            borderRadius={borderRadius}
            padding={padding}
            theme={theme}
          />
        );
      })}

      {showControls && (
        <TableCell>
          <ControlSelect onChange={handleControlChange} theme={theme}>
            <option value="">⋮</option>
            <option value="delete">Delete Row</option>
            <option value="insert">Insert Row Below</option>
          </ControlSelect>
        </TableCell>
      )}
    </TableRow>
  );
};

export default PricingTableFieldRow;

PricingTableFieldRow.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    hidden: PropTypes.bool,
    cells: PropTypes.array
  })).isRequired,
  onCellChange: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func,
  onInsertRowBelow: PropTypes.func,
  mode: PropTypes.string,
  formMode: PropTypes.string,
  isEditable: PropTypes.bool,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontColor: PropTypes.string,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  theme: PropTypes.object
};

